import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Truck, Wrench } from 'lucide-react';
import {
  useMantenimientoDashboard,
  type MantenimientoDashboard,
  type MantenimientoPend,
} from './ventasApi';
import { fmtInt } from './dashFormat';
import { EmptyCard, KpiCard, KpiSkeletonRow, PendientesList } from './dashUi';

export function MantenimientoSub({ nav }: { nav?: ReactNode }) {
  const q = useMantenimientoDashboard();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">{nav}</div>
      {q.isError ? (
        <EmptyCard>No se pudieron cargar los datos de mantenimiento.</EmptyCard>
      ) : q.isLoading || !q.data ? (
        <KpiSkeletonRow n={3} />
      ) : (
        <MantenimientoLoaded data={q.data} />
      )}
    </div>
  );
}

function MantenimientoLoaded({ data }: { data: MantenimientoDashboard }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Faltan despachar"
          value={fmtInt(data.faltaDespachar.count)}
          foot="Hornos pagados, pendientes de despacho"
          accent={data.faltaDespachar.count > 0 ? '#d03b3b' : undefined}
        />
        <KpiCard
          label="Pendiente puesta en marcha"
          value={fmtInt(data.pendientePuestaMarcha.count)}
          foot="Despachados, sin cerrar"
          accent={data.pendientePuestaMarcha.count > 0 ? '#eda100' : undefined}
        />
        <KpiCard
          label="No cerrados (total)"
          value={fmtInt(data.noCerrados)}
          foot={
            data.maxDiasPendiente > 0
              ? `Más antiguo: ${fmtInt(data.maxDiasPendiente)} días`
              : 'Todo al día'
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PendientesCard
          title="Faltan despachar"
          Icon={Truck}
          ventas={data.faltaDespachar.ventas}
          total={data.faltaDespachar.count}
        />
        <PendientesCard
          title="Pendiente puesta en marcha"
          Icon={Wrench}
          ventas={data.pendientePuestaMarcha.ventas}
          total={data.pendientePuestaMarcha.count}
        />
      </div>
    </div>
  );
}

function PendientesCard({
  title,
  Icon,
  ventas,
  total,
}: {
  title: string;
  Icon: typeof Truck;
  ventas: MantenimientoPend[];
  total: number;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
          {title}
        </CardTitle>
        <span className="text-sm text-muted-foreground">{fmtInt(total)}</span>
      </CardHeader>
      <CardContent>
        <PendientesList items={ventas} emptyText="Nada pendiente." />
        {total > ventas.length && (
          <p className="pt-3 text-xs text-muted-foreground">
            +{fmtInt(total - ventas.length)} más…
          </p>
        )}
      </CardContent>
    </Card>
  );
}
